import React from 'react'
import {
  Anchor,
  ConfirmDialog,
  Div,
  Dialog,
  Flex,
  LoadingSpinner,
  Text,
} from '@sendoutcards/quantum-design-ui'
import {
  useActions,
  useCallback,
  useFlagsWithUseQueries,
  useSelector,
  useState,
  useSubscriptions,
} from 'src/hooks'

import { LEGACY_SITE_URL } from 'src/app/api'
import { CurrentPlanCard } from './components/CurrentPlanCard'
import { PaymentSettingsCard } from './components/PaymentSettings/PaymentSettingsCard'
import {
  FullAccessFeatureType,
  Label,
  StripeSubscriptionWithItemsFragment,
} from 'src/graphql/generated/graphql'
import {
  useAccountQuery,
  useCancelStripeSubscription,
  useGetSubscriptions,
  useDefaultSubscriptionIds,
  useUpdateAccount,
  useSubscriptionAddonRemove,
  useSubscriptionAddonAdd,
  useMarketingContent,
  useDigitalCardsReceived,
} from 'src/react_query'
import { AccountSubscriptionChanger } from 'src/SubscriptionChanger/AccountSubscriptionChanger'
import { Addons } from 'src/dashboard/components'
import PromptingsUpcomingEvents from 'src/dashboard/components/PromptingsUpcomingEvents'
import { offeredAffiliateStripeSkus } from 'src/helpers/supportedUpsaleProducts'
import { AFFILIATE_BRANDING } from 'src/app/constants'
import { useFeatureAccess } from 'src/hooks/useFeatureAccess'
import { ACT_ADDON_STRIPE_ID } from 'src/marketing/plans/constants'
import { MembershipRewardsWidget } from 'src/dashboard/components/Widgets/MembershipRewards/MembershipRewardsWidget'
import { AccountSettingsWidget } from 'src/dashboard/components/Widgets/Settings/AccountSettingsWidget'
import { Button as DSButton } from 'src/design_system/components/Button/Button'
import { GiftCardsWidget } from 'src/dashboard/components/Widgets/GiftCards/GiftCardsWidget'
/**
 *
 * TODO:
 *  1. My Subscriptions Component
 * [] - New Designs for My Subscriptions
 * [] - Wire up route
 */

export const PlansAndPaymentView = () => {
  const [isChangingSubscription, setIsChangingSubscription] = useState(false)
  const [isChangingAffiliate, setIsChangingAffiliate] = useState(false)
  const { data } = useDefaultSubscriptionIds({
    suspense: true,
  })

  const defaultSubscriptionIds = data ?? []
  const [subscriptionCancelationId, setSubscriptionCancelationId] = useState<
    string | undefined
  >(undefined)
  const [subscriptionItemCancelationId, setSubscriptionItemCancelationId] =
    useState<string | undefined>(undefined)
  const [
    isRenewAffiliateConfirmDialogueOpen,
    setIsRenewAffiliateConfirmDialogueOpen,
  ] = useState(false)
  const [addingAddonToSubscriptionId, setAddingAddonSubscriptionId] = useState<
    string | undefined
  >(undefined)
  const [isShowingChangeCancelation, setIsShowingChangeCancelation] =
    useState(false)
  const { mutate: cancelSubscription, isPending } =
    useCancelStripeSubscription()
  const isMobile = useSelector(state => state.window.isMobile)
  const windowWidth = useSelector(state => state.window.width)
  const shouldStackRewards = windowWidth < 1200
  const shouldStackPurchases = windowWidth < 1100
  const actions = useActions()
  const { hasFeatureAccess: hasLifetimeAct } = useFeatureAccess([
    FullAccessFeatureType.UnlimitedActsSubDiscount,
  ])

  const updateAccount = useUpdateAccount()
  const { data: account, isLoading: isAccountLoading } = useAccountQuery()
  const { data: subscriptions, isLoading: isSubscriptionsLoading } =
    useGetSubscriptions()
  const { data: marketingContent } = useMarketingContent()
  const { canAddActAddon, hasGiftCardWallet, hasMembershipRewards } =
    useFlagsWithUseQueries()

  const giftCardsReceivedQuery = useDigitalCardsReceived(
    {
      first: 5,
      filters: {
        hasGiftCard: true,
      },
    },
    { enabled: hasGiftCardWallet },
  )
  const hasGiftCards =
    giftCardsReceivedQuery.data?.pages?.some(
      page => (page?.edges?.length ?? 0) > 0,
    ) ?? false
  const shouldShowGiftCardsWidget = hasGiftCardWallet && hasGiftCards

  const subscriptionAddonAdd = useSubscriptionAddonAdd()
  const subscriptionAddonRemove = useSubscriptionAddonRemove()

  const cancelOrUncancelSubscription = useCallback(
    async (subscriptionId: string, shouldCancel?: boolean) => {
      const sub = {
        id: subscriptionId,
        cancelAtPeriodEnd: shouldCancel ?? false,
      }

      try {
        const {
          updateAccount: { account: updatedAccount },
        } = await updateAccount.mutateAsync({
          account: {},
          subscription: sub,
        })
        actions.updatedAccount(updatedAccount)
      } catch (error) {
        console.error(error?.toString() ?? 'Failed to change subscription.')
      } finally {
        setIsChangingSubscription(false)
      }
    },
    [actions, updateAccount],
  )

  const currentPlan = account?.plan

  const rawCurrentPlanSubscription = subscriptions?.find(
    (sub: StripeSubscriptionWithItemsFragment) =>
      sub.sku === currentPlan?.stripeId,
  )
  const isAccountBasic = currentPlan?.id === '1'
  // While the subscriptions don't have the ACT Addon with the lifetime discount, we're adding it
  //  here so we show it as they technically have it.
  // TODO: remove this once all users with the lifetime subscriptions actually have the addon
  const currentPlanSubscription =
    hasLifetimeAct && rawCurrentPlanSubscription?.items.length === 1
      ? {
          ...rawCurrentPlanSubscription,
          items: rawCurrentPlanSubscription.items.concat([
            {
              id: '',
              name: 'ACT Add-On',
              sku: ACT_ADDON_STRIPE_ID,
              isAddon: true,
              undiscountedAmount: 1000,
              amount: 0,
              discounts: [
                {
                  name: 'LifetimeACT',
                },
              ],
            },
          ]),
        }
      : rawCurrentPlanSubscription
  const currentAffiliateSubscription = subscriptions?.find(
    (sub: StripeSubscriptionWithItemsFragment) =>
      offeredAffiliateStripeSkus.map(sku => sku.id).includes(sub.sku),
  )

  const { newPlan, isInPromptingsGracePeriod } = useSubscriptions()

  const subscriptionIds = defaultSubscriptionIds.map(id => ({ id }))

  const changePlanId =
    newPlan && subscriptionIds.find(sub => sub.id === newPlan.id)

  if (isAccountLoading || isSubscriptionsLoading) {
    return <LoadingSpinner size="large" />
  }
  return account ? (
    <>
      <Flex flexDirection="column" rowGap="x3" alignItems="stretch">
        <Flex flexDirection="column" rowGap="x3" alignItems="stretch">
          <Flex
            flexDirection={shouldStackRewards ? 'column' : 'row'}
            columnGap="x3"
            rowGap="x3"
            alignItems="stretch"
          >
            <Flex
              flexDirection="column"
              rowGap="x3"
              width="100%"
              style={{ flex: shouldStackRewards ? undefined : 2 }}
            >
              {isAccountBasic &&
              !currentPlanSubscription &&
              !currentAffiliateSubscription ? (
                <CurrentPlanCard
                  title={currentPlan?.title ?? 'Basic'}
                  price={currentPlan?.price.description ?? '$0.00'}
                  fillHeight={!shouldStackRewards}
                  changeAction={{
                    title: 'Change Plan',
                    onClick: () => setIsChangingSubscription(true),
                  }}
                  newPlan={changePlanId ? newPlan : undefined}
                />
              ) : null}
              {currentPlanSubscription ? (
                <CurrentPlanCard
                  title={currentPlan?.title ?? currentPlanSubscription.name}
                  price={`$${(currentPlanSubscription.total / 100).toFixed(2)}`}
                  currentSubscription={currentPlanSubscription}
                  changeAction={{
                    title: 'Change Plan',
                    onClick: () => setIsChangingSubscription(true),
                  }}
                  renewAction={
                    currentPlanSubscription.isSetToCancel
                      ? {
                          title: 'Cancel Scheduled Change',
                          onClick: () => setIsShowingChangeCancelation(true),
                        }
                      : undefined
                  }
                  cancelAction={
                    !currentPlanSubscription.isSetToCancel || newPlan
                      ? {
                          title: 'Cancel',
                          onClick: () =>
                            setSubscriptionCancelationId(
                              currentPlanSubscription.id,
                            ),
                        }
                      : undefined
                  }
                  addAddonAction={
                    // We only allow the unlimited act add-on on legacy plans
                    currentPlan && currentPlan.isLegacy && canAddActAddon
                      ? {
                          title:
                            marketingContent?.addUnlimitedActAddonButton
                              .content ?? 'Add Unlimited ACT Add-on',
                          onClick: () => {
                            actions.setPlanToDisplay('194')
                          },
                          isDisabled: subscriptionAddonAdd.isPending,
                        }
                      : undefined
                  }
                  changeItemAction={{
                    title: 'Change',
                    onClick: () => setIsChangingSubscription(true),
                  }}
                  cancelItemAction={{
                    title: 'Cancel',
                    onClick: itemId => setSubscriptionItemCancelationId(itemId),
                    isDisabled: subscriptionAddonRemove.isPending,
                  }}
                  newPlan={changePlanId ? newPlan : undefined}
                />
              ) : null}

              {currentAffiliateSubscription ? (
                <CurrentPlanCard
                  title={currentAffiliateSubscription.name}
                  price={`$${(
                    currentAffiliateSubscription.total / 100
                  ).toFixed()}`}
                  currentSubscription={currentAffiliateSubscription}
                  recurrence={
                    currentAffiliateSubscription.sku === 'GS00001'
                      ? 'month'
                      : 'year'
                  }
                  changeAction={
                    currentAffiliateSubscription.sku === 'GS00001' ||
                    currentAffiliateSubscription.sku === '14757'
                      ? {
                          title: 'Upgrade & Save',
                          onClick: () => setIsChangingAffiliate(true),
                        }
                      : undefined
                  }
                  renewAction={
                    currentAffiliateSubscription.isSetToCancel &&
                    currentAffiliateSubscription?.sku !== 'GS00016'
                      ? {
                          title: 'Cancel Scheduled Change',
                          onClick: () =>
                            setIsRenewAffiliateConfirmDialogueOpen(true),
                          isDisabled: isInPromptingsGracePeriod,
                        }
                      : undefined
                  }
                  cancelAction={
                    !currentAffiliateSubscription.isSetToCancel
                      ? {
                          title: 'Cancel',
                          onClick: () =>
                            setSubscriptionCancelationId(
                              currentAffiliateSubscription.id,
                            ),
                        }
                      : undefined
                  }
                />
              ) : null}
            </Flex>
            {hasMembershipRewards && (
              <Flex
                width="100%"
                style={{ flex: shouldStackRewards ? undefined : 1 }}
              >
                <MembershipRewardsWidget amount={account.points?.amount ?? 0} />
              </Flex>
            )}
          </Flex>
          <Flex
            flexDirection={shouldStackRewards ? 'column' : 'row'}
            columnGap="x3"
            rowGap="x3"
            alignItems="stretch"
          >
            <Flex
              width="100%"
              style={{ flex: shouldStackRewards ? undefined : 2 }}
            >
              <PaymentSettingsCard />
            </Flex>
            {shouldShowGiftCardsWidget && (
              <Flex
                width="100%"
                style={{ flex: shouldStackRewards ? undefined : 1 }}
              >
                <GiftCardsWidget />
              </Flex>
            )}
            <Flex
              width="100%"
              style={{ flex: shouldStackRewards ? undefined : 1 }}
            >
              <AccountSettingsWidget
                onToggleAccountDrawer={() =>
                  actions.openDashboardSettingsRoute()
                }
              />
            </Flex>
          </Flex>
          {/* <PurchasesCard /> */}
        </Flex>
        <Flex
          flexDirection={shouldStackPurchases ? 'column' : 'row'}
          columnGap="x3"
          rowGap="x3"
          alignItems="stretch"
        >
          <Flex
            width="100%"
            style={{ flex: shouldStackPurchases ? undefined : 1 }}
          >
            <Flex
              flexDirection="column"
              width="100%"
              backgroundColor="foreground"
              borderRadius="32px"
              inset={{ vertical: 'x2', horizontal: 'x2_5' }}
              boxShadow="light"
              rowGap="x2"
            >
              <Flex
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                columnGap="x2"
                rowGap="x1"
              >
                <Div>
                  <Text
                    type="body"
                    color="primaryHeading"
                    weight="bold"
                    content="My Account Purchases"
                  />
                  <Text
                    type="caption"
                    content="Plans, add-ons, and usage at a glance."
                    style={{ color: '#6b7280' }}
                  />
                </Div>
                <Flex columnGap="x1" rowGap="x1" flexWrap="wrap">
                  {account.isAffiliate && (
                    <DSButton
                      title="Promptings PRO"
                      onClick={() =>
                        window.open(account.promptingProLoginUrl, '_blank')
                      }
                      backgroundColor="#f3f4f6"
                      textColor="#111827"
                      textSize="12px"
                      textWeight={600}
                      padding="8px 14px"
                      border="1px solid #e5e7eb"
                      borderRadius="999px"
                      width="auto"
                      whiteSpace="nowrap"
                    />
                  )}
                  {!account.labels.includes(
                    Label.ShouldHideRevolutionOffice,
                  ) && (
                    <DSButton
                      title={
                        account.isAffiliate
                          ? 'Revolution Office'
                          : 'Invoice History'
                      }
                      onClick={() =>
                        window.open(
                          `${LEGACY_SITE_URL}/bd/backoffice`,
                          '_blank',
                        )
                      }
                      backgroundColor="#f3f4f6"
                      textColor="#111827"
                      textSize="12px"
                      textWeight={600}
                      padding="8px 14px"
                      border="1px solid #e5e7eb"
                      borderRadius="999px"
                      width="auto"
                      whiteSpace="nowrap"
                    />
                  )}
                </Flex>
              </Flex>
              <Div
                style={{
                  backgroundColor: '#EEF0F2',
                  borderRadius: 20,
                  padding: '12px 16px',
                }}
              >
                <Addons />
              </Div>
              {isMobile && (
                <Flex flexDirection="row" justifyContent="center">
                  <Anchor
                    isDecorated={true}
                    color="anchorBlue"
                    onClick={() => actions.openPricing()}
                  >
                    Pricing
                  </Anchor>
                </Flex>
              )}
              <AccountSubscriptionChanger
                shouldHideCurrentPlan={true}
                subscriptionPlanIds={[{ id: '165' }]}
                isOpen={isChangingAffiliate}
                onClose={() => setIsChangingAffiliate(false)}
                isChangingAffiliate={isChangingAffiliate}
              />
              <AccountSubscriptionChanger
                subscriptionPlanIds={subscriptionIds}
                isOpen={isChangingSubscription}
                onClose={() => setIsChangingSubscription(false)}
              />
            </Flex>
          </Flex>
          <Flex
            width="100%"
            style={{ flex: shouldStackPurchases ? undefined : 1 }}
          >
            <PromptingsUpcomingEvents />
          </Flex>
        </Flex>
      </Flex>
      <ConfirmDialog
        isOpen={isRenewAffiliateConfirmDialogueOpen}
        title={`Renew your ${AFFILIATE_BRANDING.capitalized} subscription`}
        description={`Add the ${AFFILIATE_BRANDING.capitalized} subscription of $14/month to my account so I can start my business today.`}
        accept={{
          title: 'Confirm renewal',
          onClick: () => {
            cancelOrUncancelSubscription(
              currentAffiliateSubscription?.id ?? '',
              false,
            )
            cancelSubscription(
              { id: currentAffiliateSubscription?.id ?? '' },
              {
                onSuccess(data) {
                  // TODO: React Query Refactor, once redux account is removed we can just remove this
                  actions.updatedAccount(data.account)
                },
                onError(error) {
                  console.log(error)
                },
              },
            )
            setIsRenewAffiliateConfirmDialogueOpen(false)
            setIsChangingSubscription(false)
          },
        }}
        decline={{
          title: 'Cancel',
          onClick: () => {
            setIsRenewAffiliateConfirmDialogueOpen(false)
          },
        }}
        zIndex={9000}
        primaryAction={'accept'}
        onClose={() => setIsRenewAffiliateConfirmDialogueOpen(false)}
      />
      {!!subscriptionCancelationId &&
        (() => {
          const title = hasMembershipRewards
            ? (marketingContent?.cancelSubscriptionDialogTitle.content ??
              'Cancel Subscription')
            : 'Cancel Subscription'
          const body = hasMembershipRewards
            ? (
                marketingContent?.cancelSubscriptionDialogBody.content ??
                'Are you sure you want to cancel this subscription?'
              ).replaceAll('{rewards}', account.points.amount.toString())
            : 'Are you sure you want to cancel this subscription?'
          return (
            <ConfirmDialog
              isOpen={true}
              title={title}
              description={body}
              decline={{
                title: 'Keep Subscription',
                onClick: () => setSubscriptionCancelationId(undefined),
              }}
              accept={{
                title: 'Cancel Subscription',
                onClick: () => {
                  if (!subscriptionCancelationId) return
                  cancelOrUncancelSubscription(subscriptionCancelationId, true)
                  setSubscriptionCancelationId(undefined)
                },
              }}
              zIndex={1}
            />
          )
        })()}
      {subscriptionItemCancelationId && (
        <ConfirmDialog
          isOpen={true}
          title="Cancel Add-on"
          description="Are you sure you want to cancel this add-on?"
          decline={{
            title: 'Keep Add-on',
            onClick: () => setSubscriptionItemCancelationId(undefined),
          }}
          accept={{
            title: 'Cancel Add-on',
            onClick: async () => {
              await subscriptionAddonRemove.mutateAsync({
                subscriptionItemId: subscriptionItemCancelationId,
              })
              setSubscriptionItemCancelationId(undefined)
            },
            isDisabled: subscriptionAddonRemove.isPending,
          }}
          zIndex={1}
        />
      )}
      {addingAddonToSubscriptionId && (
        <ConfirmDialog
          isOpen={true}
          title={
            marketingContent?.addUnlimitedActAddonConfirmationTitle.content ??
            'Add Unlimited ACT Add-on'
          }
          description={
            marketingContent?.addUnlimitedActAddonConfirmationDescription
              .content ??
            'Do you want to add the Unlimited ACT Add-on for $10 / month?'
          }
          primaryAction="accept"
          accept={{
            title:
              marketingContent?.addUnlimitedActAddonConfirmationAccept
                .content ?? 'Yes',
            onClick: async () => {
              await subscriptionAddonAdd.mutateAsync({
                subscriptionId: addingAddonToSubscriptionId,
                sku: ACT_ADDON_STRIPE_ID,
              })
              setAddingAddonSubscriptionId(undefined)
            },
            isDisabled: subscriptionAddonAdd.isPending,
          }}
          decline={{
            title:
              marketingContent?.addUnlimitedActAddonConfirmationDecline
                .content ?? 'No',
            onClick: () => setAddingAddonSubscriptionId(undefined),
          }}
          zIndex={1}
        />
      )}
      <ConfirmDialog
        isOpen={isShowingChangeCancelation}
        title={`Keep my ${currentPlan?.price.description} ${currentPlan?.title} Subscription`}
        description="Click below to keep your plan and continue utilizing the features you enjoy."
        decline={{
          title: 'Close',
          onClick: () => setIsShowingChangeCancelation(false),
        }}
        accept={{
          title: 'Keep Current Plan!',
          onClick: () => {
            cancelOrUncancelSubscription(
              currentPlanSubscription?.id ?? '',
              false,
            )
            setIsShowingChangeCancelation(false)
          },
        }}
        primaryAction="accept"
        zIndex={1}
      />
      <Dialog isOpen={isPending}>
        <Text
          type="largeBody"
          content={'Confirming your Subscription Changes...'}
        />
      </Dialog>
    </>
  ) : (
    <Text type="body" content="login to view account" />
  )
}
